/**
 * The memo's company facts (DOP-616): the same fields on every memo, as a
 * two-column list of label and value in the new memo style. It replaces the
 * bordered grey grid the firm's Word memos use; the export keeps that.
 *
 * A field no file answered reads "Not found", which is its value and not an
 * empty cell. It carries a "Must have" pill only while its whole block group
 * is still open (transaction type OR raise size: either answered satisfies
 * both). A classified field (sector, business model, website, headquarters)
 * reads like any other: its source chip shows the facts it was worked out from.
 *
 * The Company line is the deal's operating name, the name the deal was added
 * under, with the legal name the materials state under it when it differs
 * (DOP-513): "Bradley Management LLC" is what the contract says, "Dietitian
 * Live" is the company.
 *
 * A fact of one day (a share price, a market cap, a NAV per share) is
 * labelled with that day when its source dated it (DOP-661).
 */

import React from "react";

import type {
  DealScreeningFact,
  DealScreeningSource,
} from "../../types/dealScreening";
import { headerFactLabel } from "./dealScreeningFormat";
import FactValue from "./FactValue";
import { renderInline, stripMarks } from "./inlineMarks";
import { factElementId, openBlockGroups } from "./memoModel";

export interface HeaderBlockTableProps {
  facts: DealScreeningFact[];
  /** The deal's operating name; the Company line prints it. */
  dealName: string;
  onOpenSource: (source: DealScreeningSource) => void;
  /** Opens a person's own answer for editing. */
  onEditFact?: (fact: DealScreeningFact) => void;
  /** Opens an open conflict on the Gaps tab. */
  onOpenConflict?: (fact: DealScreeningFact) => void;
  /** The block's heading, as a person may have renamed it (DOP-641). */
  title?: string;
  /** Every fact of the run by slot, for a computed value's popover (DOP-661). */
  factsBySlot?: ReadonlyMap<string, DealScreeningFact>;
  /** The fact whose other readings open unfolded (a jump from Sources read). */
  expandFactKey?: string | null;
}

const COMPANY_KEY = "company";

/** Whether the stated value already IS the operating name. */
const statesTheDealName = (
  fact: DealScreeningFact,
  dealName: string,
): boolean =>
  fact.value !== null &&
  fact.state !== "gap" &&
  fact.state !== "not_available" &&
  String(fact.value).trim().toLowerCase() === dealName.trim().toLowerCase();

/** A person answered it: entered a value or accepted it is not available. */
const answered = (fact: DealScreeningFact): boolean =>
  fact.entered_by_user_id !== null ||
  fact.entered_by_name !== null ||
  fact.entered_at !== null;

const HeaderBlockTable: React.FC<HeaderBlockTableProps> = ({
  facts,
  dealName,
  onOpenSource,
  onEditFact,
  onOpenConflict,
  factsBySlot,
  title = "Company facts",
  expandFactKey = null,
}) => {
  const fields = facts
    .filter((fact) => fact.kind === "header")
    .sort((a, b) => a.sort_order - b.sort_order);
  const open = openBlockGroups(facts);

  return (
    <section aria-label={stripMarks(title)} className="font-sans">
      <h3 className="mb-3 font-serif text-[22px] font-bold leading-snug tracking-[-0.005em] text-[#004C4B]">
        {renderInline(title)}
      </h3>
      <dl
        className="grid overflow-hidden rounded-xl border border-[#e7e7e4] md:grid-cols-2"
        data-testid="header-block"
      >
        {fields.map((fact) => {
          const missing =
            fact.state === "gap" || fact.state === "not_available";
          const blocking =
            fact.state === "gap" &&
            fact.policy === "blocks" &&
            open.has(fact.block_group ?? fact.key);
          const legalName =
            fact.key === COMPANY_KEY &&
            !missing &&
            !statesTheDealName(fact, dealName);
          return (
            <div
              key={fact.key}
              id={factElementId(fact.key)}
              className="flex min-w-0 gap-3 border-b border-[#f1f2f4] px-3.5 py-2 md:[&:nth-child(odd)]:border-r"
            >
              <dt className="w-[104px] shrink-0 pt-px text-[12px] text-[#6B7280]">
                {headerFactLabel(fact)}
              </dt>
              <dd className="min-w-0 flex-1 text-[13.5px] leading-[1.45] text-[#1F2937]">
                {missing && !answered(fact) ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-[#9a9ea3]">Not found</span>
                    {blocking && (
                      <span className="rounded-full border border-[#fedf89] bg-[#fffaeb] px-2 py-px text-[11px] font-semibold text-[#b54708]">
                        Must have
                      </span>
                    )}
                  </span>
                ) : (
                  <>
                    {legalName && (
                      <span className="font-medium" data-testid="company-name">
                        {dealName}
                      </span>
                    )}
                    {legalName && (
                      <span className="text-[12px] text-[#6B7280]">
                        {" "}
                        · legal name:{" "}
                      </span>
                    )}
                    <FactValue
                      fact={fact}
                      variant="text"
                      onOpenSource={onOpenSource}
                      blocking={blocking}
                      onEdit={onEditFact}
                      onOpenConflict={onOpenConflict}
                      factsBySlot={factsBySlot}
                      expandOtherWordings={expandFactKey === fact.key}
                      compact
                    />
                  </>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
};

export default HeaderBlockTable;
